// The fleet-wide list of travel-log entries on the Trips page: every item's
// Captain's / Pilot's Log entries in one table, newest first, with an Item
// column saying whose voyage each row is.
//
// Not LogbookEntriesTable: that one's columns come from a single logbook
// TYPE's registry, and this list mixes types and items. The row is instead
// LogbookEntrySummary's row as a <tr> -- date, the entry's title, a Details
// arrow -- plus the Item column, and minus the delete button (the entry page
// has one; a fleet list is not where a record should be destroyed from).
//
// `rows` are `{ entry, item, logbook }`, already sorted and capped by the
// page. `onSelect(row)` gets the same object from the row click and from the
// Details arrow, so there is one navigation rule.
//
// The title is read off a DISPLAY copy (entryWithResolvedEndpoints), never
// the stored entry: an entry's From/To live in its `fields` blob as a mirror
// of its route, so a place renamed in /me/places since is reachable only
// through the waypoint's placeId. `placeIndex` is the page's, as always.
//
// No role="button" on the row: it carries a Link and a Button, and an ARIA
// button's children are presentational, so the role would hide exactly the
// controls that are reachable (the LogbookEntriesTable rule).
//
// WIDE MODE. `wide` adds what the per-trip logbook page shows and a phone
// cannot fit: an Hours column, and the notes and photos of LogbookEntriesTable
// -- `showNotes` off keeps its compact "has notes" icon column, on drops the
// column and carries the note in full on a continuation <tr>; `photosByEntry`
// puts that entry's strip on the SAME continuation row (see that table's
// header for why not a third <tr>; App.css's leg-row / leg-note-row pairing is
// reused verbatim). Hours is per ROW: a Captain's entry's `hours` and a
// Pilot's `pic` share the column, resolved through hoursFieldForType so the
// key is never guessed here.
//
// The PAGE owns the breakpoint and passes `wide`, rather than this table
// calling useIsMdUp itself (TripGroup's shape, not LogbookSummaryCard's):
// the page also gates the Show notes / Show photos switches and the S3 read
// on the same answer, and a table deciding on its own could disagree with
// the switches above it. Every wide-mode prop defaults to absent, which is
// what leaves the narrow tree -- and this table's existing suite -- exactly
// as it was.

import React from 'react';
import Button from 'react-bootstrap/Button';
import Table from 'react-bootstrap/Table';
import { Link } from 'react-router-dom';
import { shortDisplayDate } from '../../fleet-shared/Common.mjs';
import {
  entryHasNotes,
  entryTitle,
  parseEntryFields,
  readEntryField,
} from '../../fleet-shared/LogbookTypes.mjs';
import { hoursFieldForType } from '../../fleet-shared/LogbookTrips.mjs';
import { entryWithResolvedEndpoints } from '../../fleet-shared/TripRoute.mjs';
import { urlForItem } from '../../fleet-shared/Urls.mjs';
import EntryPhotoStrip from '../logbook/EntryPhotoStrip';

// The row's hours as the logbook list shows them -- the stored value as
// LogbookEntriesTable's cellValue hands it back, '' for none. Not
// displayHours: that rounds a SUM, and a row is a record.
function hoursText(entry, typeId) {
  const field = hoursFieldForType(typeId);
  if (!field) return '';
  const value = readEntryField(entry, field, parseEntryFields(entry));
  return value === null || value === undefined ? '' : value;
}

const EntryRow = ({ row, placeIndex, onSelect, wide, showNotes, photosByEntry, onOpenPhoto, span }) => {
  const { entry, item, logbook } = row;
  const title = entryTitle(
    entryWithResolvedEndpoints(entry, placeIndex),
    logbook?.logbookType,
    { placeIndex }
  );
  const note = wide && entryHasNotes(entry) ? entry.notes.trim() : '';
  const withNote = Boolean(showNotes) && note !== '';
  const photos = wide ? photosByEntry?.get(entry.id) ?? [] : [];
  const withPhotos = photos.length > 0;
  const withContinuation = withNote || withPhotos;

  return (
    // A fragment keyed by the entry: the note and the photos are a second
    // <tr> because they have to span every column.
    <React.Fragment>
      <tr
        onClick={() => onSelect(row)}
        style={{ cursor: 'pointer' }}
        // A row WITHOUT a continuation must not get `leg-row`, or its bottom
        // rule would vanish.
        className={withContinuation ? 'leg-row' : undefined}
      >
        <td className="align-middle entered-data text-nowrap">{shortDisplayDate(entry.date)}</td>
        <td className="align-middle entered-data">
          {/* stopPropagation: the link goes to the ITEM, the row to the entry. */}
          <Link to={urlForItem(item.id)} onClick={(e) => e.stopPropagation()}>
            {item.name}
          </Link>
        </td>
        <td className="align-middle entered-data">{title}</td>
        {wide && (
          <td className="align-middle entered-data">{hoursText(entry, logbook?.logbookType)}</td>
        )}
        {wide && !showNotes && (
          <td className="align-middle text-center">
            {note !== '' && (
              <i
                className="fa-solid fa-book"
                title="Has notes"
                aria-label="Has notes"
                role="img"
              ></i>
            )}
          </td>
        )}
        <td className="align-middle text-end" style={{ width: 56 }}>
          <Button
            variant="outline-primary"
            size="sm"
            aria-label="Details"
            onClick={(e) => { e.stopPropagation(); onSelect(row); }}
          >
            <i className="fa-solid fa-chevron-right" aria-hidden="true"></i>
          </Button>
        </td>
      </tr>
      {withContinuation && (
        <tr onClick={() => onSelect(row)} style={{ cursor: 'pointer' }} className="leg-note-row">
          <td colSpan={span} className="small text-body-secondary pt-0">
            {/* pre-wrap: a note typed as several lines stays that way. On its
                own div so it doesn't govern the strip. */}
            {withNote && <div style={{ whiteSpace: 'pre-wrap' }}>{note}</div>}
            {withPhotos && (
              <EntryPhotoStrip
                photos={photos}
                onOpenPhoto={onOpenPhoto}
                // The same destination as the row click -- one navigation rule.
                onOpenEntry={() => onSelect(row)}
              />
            )}
          </td>
        </tr>
      )}
    </React.Fragment>
  );
};

const FleetEntriesTable = ({
  rows,
  placeIndex,
  onSelect,
  wide = false,
  showNotes = false,
  photosByEntry = null,
  onOpenPhoto,
}) => {
  // Date, Item, Entry, Details are always there; wide adds Hours, and the
  // Notes icon column only while the notes themselves are not shown. The
  // continuation row has to span exactly that, LogbookEntriesTable's sum.
  const span = 4 + (wide ? 1 : 0) + (wide && !showNotes ? 1 : 0);

  return (
    <Table responsive hover className="align-middle mb-0">
      <thead className="bg-body-tertiary">
        <tr>
          <th scope="col">Date</th>
          <th scope="col">Item</th>
          <th scope="col">Entry</th>
          {wide && <th scope="col">Hours</th>}
          {wide && !showNotes && <th scope="col" className="text-center">Notes</th>}
          <th scope="col" className="text-end">Details</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <EntryRow
            key={row.entry.id}
            row={row}
            placeIndex={placeIndex}
            onSelect={onSelect}
            wide={wide}
            showNotes={showNotes}
            photosByEntry={photosByEntry}
            onOpenPhoto={onOpenPhoto}
            span={span}
          />
        ))}
      </tbody>
    </Table>
  );
};

export default FleetEntriesTable;
